import './App.css';
import ShowCard from './components/content/ShowCard';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useSearchParams,
  useNavigate
} from "react-router-dom";
import CardState from './context/cards/CardState';
import ScrollState from './context/scroll/ScrollState';
import DashboardState from './context/dashboard/DashboardState';
import React, { useState, useEffect, useContext } from 'react';
import ScrollSpy from './components/scrollSpy/ScrollSpy';
import Footer from './components/footer/Footer';
import FloatingWaiterButton from './components/waiter/FloatingWaiterButton';
import CartPage from './components/cart/CartPage';
import LoginPage from './components/auth/LoginPage';
import OTPVerification from './components/auth/OTPVerification';
import CustomerNameModal from './components/auth/CustomerNameModal';
import StaffLoginPage from './components/auth/StaffLoginPage';
import AdminDashboard from './components/dashboard/admin/AdminDashboard';
import OwnerDashboard from './components/dashboard/owner/OwnerDashboard';
import ReportGenerator from './components/dashboard/reports/ReportGenerator';
import ChefDashboard from './components/dashboard/chef/ChefDashboard';
import WaiterDashboard from './components/dashboard/waiter/WaiterDashboard';
import authService from './services/api/authService';
import staffAuthService from './services/api/staffAuthService';
import menuService from './services/api/menuService';
import { setRastId } from './utils/rastIdManager';
import cardContext from './context/cards/cardContext';

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const StaffProtectedRoute = ({ children, allowedRoles, loginPath }) => {
  const isAuthenticated = staffAuthService.isAuthenticated(allowedRoles);

  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  return children;
};

const MainContent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cardData, loading } = useContext(cardContext);
  const [sectionList, setSectionList] = useState([]);
  const [activeFilter, setActiveFilter] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [creatingDiningSession, setCreatingDiningSession] = useState(false);
  const [diningSessionError, setDiningSessionError] = useState('');

  useEffect(() => {
    if (cardData && cardData.length > 0) {
      const categories = menuService.extractCategories(
        cardData.map(item => ({
          ...item,
          category: item.category.charAt(0).toUpperCase() + item.category.slice(1).replace(/-/g, ' ')
        }))
      );
      setSectionList(categories);
      if (categories.length > 0) {
        setActiveFilter(categories[0].id);
      }
    }
  }, [cardData]);

  useEffect(() => {
    const hasTicket = authService.hasCustomerTicket();
    const showModal = searchParams.get('showNameModal') === 'true';
    const customerData = authService.getCustomerData();
    
    if (hasTicket) {
      setShowNameModal(false);
      if (showModal) {
        navigate('/', { replace: true });
      }
      return;
    }

    if (customerData.name) {
      let isActive = true;

      const createDiningSession = async () => {
        setCreatingDiningSession(true);
        setDiningSessionError('');

        const result = await authService.createCustomerTicket(
          customerData.name,
          customerData.phoneNumber,
          customerData.email,
          customerData.rastId
        );

        if (!isActive) return;

        if (result.success) {
          setShowNameModal(false);
          navigate('/', { replace: true });
        } else {
          setDiningSessionError(result.error || 'Failed to create dining session. Please refresh and try again.');
          setShowNameModal(true);
        }

        setCreatingDiningSession(false);
      };

      createDiningSession();

      return () => {
        isActive = false;
      };
    }

    if (!hasTicket || showModal) {
      setShowNameModal(true);
    }
  }, [searchParams, navigate]);

  const handleScroll = () => {
    const sectionOffsets = sectionList.map(filter => {
      const section = document.getElementById(filter.id);
      return section ? { id: filter.id, offset: section.offsetTop } : null;
    }).filter(Boolean);

    if (sectionOffsets.length === 0) return;

    const currentSection = sectionOffsets.reduce((prev, curr) => {
      return window.scrollY >= curr.offset - 50 ? curr : prev;
    });
    
    if (currentSection) {
      setActiveFilter(currentSection.id);
    }
  };

  useEffect(() => {
    if (sectionList.length > 0) {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [sectionList]);

  const handleNameModalSuccess = () => {
    setShowNameModal(false);
    navigate('/', { replace: true });
  };

  const renderNameModal = () => (
    <CustomerNameModal 
      isOpen={showNameModal}
      onClose={() => {}}
      onSuccess={handleNameModalSuccess}
    />
  );

  if (loading) {
    return (
      <div className="loading-container">
        {renderNameModal()}
        <p>Loading menu...</p>
      </div>
    );
  }

  if (sectionList.length === 0) {
    if (showNameModal || creatingDiningSession) {
      return (
        <>
          {renderNameModal()}
          {creatingDiningSession && (
            <div className="loading-container">
              <p>Starting your dining session...</p>
            </div>
          )}
        </>
      );
    }

    return (
      <div className="error-container">
        <p>{diningSessionError || 'No menu items available'}</p>
      </div>
    );
  }

  return (
    <>
      {renderNameModal()}
      {creatingDiningSession && (
        <div className="loading-container">
          <p>Starting your dining session...</p>
        </div>
      )}
      <ScrollSpy
        filterList={sectionList}
        activeFilter={activeFilter}
        onFilterClick={filterId =>
          document.getElementById(filterId).scrollIntoView({ behavior: 'smooth' })
        }
      />
      <div className="content">
        <ShowCard sectionList={sectionList}/>
      </div>
      <FloatingWaiterButton/>
      <Footer/>
    </>
  );
};

const LoginRedirect = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const rastIdFromUrl = searchParams.get('rastId');
    
    if (rastIdFromUrl) {
      setRastId(rastIdFromUrl);
    }

    const isAuthenticated = authService.isAuthenticated();
    
    if (isAuthenticated) {
      navigate('/', { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  }, [searchParams, navigate]);

  return null;
};

function App() {
  return (
    <>
      <DashboardState>
        <CardState>
          <ScrollState>
            <div className="App-body">
              <Router>
                <Routes>
                  <Route path="/" element={
                    <ProtectedRoute>
                      <MainContent />
                    </ProtectedRoute>
                  } />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/verify-otp" element={<OTPVerification />} />
                  <Route path="/owner/login" element={<StaffLoginPage role="OWNER" />} />
                  <Route path="/chef/login" element={<StaffLoginPage role="CHEF" />} />
                  <Route path="/waiter/login" element={<StaffLoginPage role="WAITER" />} />
                  <Route path="/cart" element={
                    <ProtectedRoute>
                      <CartPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/start" element={<LoginRedirect />} />
                  
                  {/* Dashboard Routes */}
                  <Route path="/dashboard/admin" element={
                    <StaffProtectedRoute allowedRoles={['OWNER', 'MANAGER']} loginPath="/owner/login">
                      <AdminDashboard />
                    </StaffProtectedRoute>
                  } />
                  <Route path="/dashboard/owner" element={
                    <StaffProtectedRoute allowedRoles={['OWNER']} loginPath="/owner/login">
                      <OwnerDashboard />
                    </StaffProtectedRoute>
                  } />
                  <Route path="/dashboard/reports" element={
                    <StaffProtectedRoute allowedRoles={['OWNER', 'MANAGER']} loginPath="/owner/login">
                      <ReportGenerator />
                    </StaffProtectedRoute>
                  } />
                  <Route path="/dashboard/chef" element={
                    <StaffProtectedRoute allowedRoles={['CHEF']} loginPath="/chef/login">
                      <ChefDashboard />
                    </StaffProtectedRoute>
                  } />
                  <Route path="/dashboard/chef/:workerId" element={
                    <StaffProtectedRoute allowedRoles={['CHEF']} loginPath="/chef/login">
                      <ChefDashboard />
                    </StaffProtectedRoute>
                  } />
                  <Route path="/dashboard/waiter" element={
                    <StaffProtectedRoute allowedRoles={['WAITER']} loginPath="/waiter/login">
                      <WaiterDashboard />
                    </StaffProtectedRoute>
                  } />
                </Routes>
              </Router>
            </div>
          </ScrollState>
        </CardState>
      </DashboardState>
    </>
  );
}

export default App;
